import { Link } from "react-router-dom";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import PageSEO from "@/components/PageSEO";

const TODAY = new Date().toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" });

export default function PrivacyNotice() {
  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="page-privacy">
      <PageSEO
        title="Aviso de Privacidad"
        description="Aviso de Privacidad de Villas Pura Vida conforme a la LFPDPPP. Cómo recabamos, usamos y protegemos tus datos personales al reservar hospedaje en Barra de Galindo, Tuxpan."
        path="/aviso-de-privacidad"
      />
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 md:px-10 py-20 md:py-28">
        <p className="text-eyebrow text-[#E07A5F] mb-4">Legal</p>
        <h1 className="font-serif-display text-5xl md:text-6xl leading-none mb-6">Aviso de Privacidad</h1>
        <p className="text-stone-500 text-sm mb-12">Última actualización: {TODAY}</p>

        <article className="prose prose-stone max-w-none space-y-8 text-stone-700 leading-relaxed">

          <Section title="1. Identidad y domicilio del responsable">
            <p><b>Villas Pura Vida</b> (en adelante "Villas Pura Vida", "nosotros" o "el responsable"), con domicilio en <b>Playa Barra de Galindo, Tuxpan, Veracruz, México</b>, es responsable del tratamiento de tus datos personales conforme a la Ley Federal de Protección de Datos Personales en Posesión de los Particulares (LFPDPPP), su Reglamento y demás normatividad aplicable en México.</p>
            <p>Contacto del responsable:<br/>
            Correo: <a href="mailto:reservas@puravidatuxpan.com" className="text-[#E07A5F] underline">reservas@puravidatuxpan.com</a><br/>
            WhatsApp: <a href="https://wa.me/5217711290073" target="_blank" rel="noopener" className="text-[#E07A5F] underline">+521 7711290073</a></p>
          </Section>

          <Section title="2. Datos personales que recabamos">
            <p>Recabamos directamente de ti los siguientes datos personales para los fines descritos en el presente aviso:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Datos de identificación:</b> nombre completo.</li>
              <li><b>Datos de contacto:</b> correo electrónico, número de teléfono / WhatsApp.</li>
              <li><b>Datos de la reserva:</b> fechas de estancia, número de huéspedes, mensajes y preferencias.</li>
              <li><b>Datos de pago:</b> únicamente recabados y procesados por <b>Stripe Inc.</b>; Villas Pura Vida NO almacena tu número de tarjeta ni CVV.</li>
              <li><b>Datos técnicos:</b> dirección IP, navegador y datos analíticos básicos al visitar el sitio.</li>
            </ul>
            <p>No recabamos datos personales sensibles.</p>
          </Section>

          <Section title="3. Finalidades del tratamiento">
            <p><b>Finalidades primarias</b> (necesarias para la relación):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Procesar tu reservación y cobrarte mediante Stripe.</li>
              <li>Enviarte confirmaciones, recordatorios y guías de llegada por correo electrónico.</li>
              <li>Coordinar con el encargado de la propiedad tu check-in y check-out.</li>
              <li>Cumplir obligaciones fiscales y legales aplicables.</li>
              <li>Atender dudas, comentarios o solicitudes que nos envíes.</li>
            </ul>
            <p><b>Finalidades secundarias</b> (opcionales; puedes oponerte sin afectar tu reserva):</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Enviarte promociones, descuentos y novedades de Villas Pura Vida.</li>
              <li>Estadísticas de uso del sitio para mejorar la experiencia.</li>
            </ul>
            <p>Si deseas oponerte a las finalidades secundarias, escríbenos a <a href="mailto:reservas@puravidatuxpan.com" className="text-[#E07A5F] underline">reservas@puravidatuxpan.com</a> con el asunto "Oposición de uso secundario".</p>
          </Section>

          <Section title="4. Transferencias de datos">
            <p>Compartimos tus datos personales únicamente con los siguientes terceros, sin requerir tu consentimiento adicional conforme al artículo 37 de la LFPDPPP:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><b>Stripe, Inc.</b> — procesamiento de pagos.</li>
              <li><b>Resend, Inc.</b> — envío de correos transaccionales.</li>
              <li><b>Cloudflare, Inc.</b> — infraestructura y seguridad del dominio.</li>
              <li><b>Encargado(s) operativo(s)</b> de la propiedad rentada, exclusivamente para coordinar tu llegada.</li>
              <li><b>Autoridades fiscales o judiciales</b> cuando exista requerimiento legal.</li>
            </ul>
            <p>No vendemos, alquilamos ni cedemos tus datos personales con fines comerciales a terceros distintos a los anteriores.</p>
          </Section>

          <Section title="5. Derechos ARCO">
            <p>En cualquier momento puedes ejercer tus derechos de <b>Acceso, Rectificación, Cancelación u Oposición</b> (ARCO) al tratamiento de tus datos, así como revocar tu consentimiento, enviando un correo a <a href="mailto:reservas@puravidatuxpan.com" className="text-[#E07A5F] underline">reservas@puravidatuxpan.com</a> con la siguiente información:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Nombre completo y medio de contacto para respuesta.</li>
              <li>Identificación oficial vigente (INE, pasaporte) adjunta.</li>
              <li>Descripción clara del derecho que deseas ejercer y los datos involucrados.</li>
            </ul>
            <p>Responderemos en un plazo máximo de <b>20 días hábiles</b>. Cuando proceda, la acción se ejecutará dentro de los 15 días hábiles posteriores a la respuesta.</p>
          </Section>

          <Section title="6. Conservación de los datos">
            <p>Conservamos tus datos personales durante el tiempo necesario para cumplir las finalidades descritas, así como los plazos legales fiscales (mínimo 5 años para registros contables). Una vez transcurrido este periodo, los datos se eliminan o anonimizan de manera segura.</p>
          </Section>

          <Section title="7. Uso de cookies y tecnologías similares">
            <p>El sitio utiliza cookies estrictamente necesarias para el funcionamiento (sesión, idioma) y almacenamiento local del navegador para guardar tu progreso en el quiz interactivo y la oferta del 10%. No usamos cookies publicitarias de terceros.</p>
            <p>Puedes deshabilitar las cookies desde la configuración de tu navegador; sin embargo, algunas funciones podrían no operar correctamente.</p>
          </Section>

          <Section title="8. Seguridad">
            <p>Implementamos medidas administrativas, físicas y técnicas razonables para proteger tus datos contra daño, pérdida, alteración, destrucción o uso no autorizado. Los pagos se procesan vía Stripe bajo cifrado TLS y certificación PCI-DSS Nivel 1.</p>
          </Section>

          <Section title="9. Modificaciones al aviso">
            <p>Nos reservamos el derecho de actualizar este Aviso de Privacidad cuando sea necesario por cambios legales, operativos o tecnológicos. Cualquier modificación se publicará en esta misma página con la fecha de actualización al inicio del documento.</p>
          </Section>

          <Section title="10. Autoridad reguladora">
            <p>Si consideras que tu derecho a la protección de datos personales ha sido lesionado, puedes acudir al Instituto Nacional de Transparencia, Acceso a la Información y Protección de Datos Personales (INAI) en <a href="https://home.inai.org.mx" target="_blank" rel="noopener" className="text-[#E07A5F] underline">home.inai.org.mx</a>.</p>
          </Section>

          <p className="text-stone-500 text-sm pt-8 border-t border-stone-200">Al continuar utilizando este sitio y/o completar una reservación, manifiestas haber leído, entendido y aceptado los términos del presente Aviso de Privacidad.</p>
        </article>

        <div className="mt-16 text-center">
          <Link to="/" className="text-eyebrow text-[#E07A5F] hover:underline">← Volver al inicio</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <h2 className="font-serif-display text-2xl text-stone-900 mb-4">{title}</h2>
      <div className="space-y-3 text-base">{children}</div>
    </section>
  );
}
